import React, { useState } from "react";
import { motion } from "framer-motion";
import { DollarSign, Clock, AlertTriangle, Plus, CreditCard, Star } from "lucide-react";
import PaginatedInvoiceTable from "./PaginatedInvoiceTable";
import InvoiceStatusFilter from "./InvoiceStatusFilter";

const BillingSection = ({
  // The data and callbacks you need:
  client, // full client object (for saved payment methods display)
  invoices, // array of invoice objects
  stats, // object { totalPaid, totalPending, overdue, upcomingPayments }
  isLoading, // for invoice table loading
  isPublishing, // loading state for publishing
  isUnpublishing, // loading state for unpublishing
  publishingInvoiceId, // ID of invoice being published
  unpublishingInvoiceId, // ID of invoice being unpublished
  onCreateInvoice, // callback when user clicks "Create Invoice" button
  onViewInvoice, // callback for "View Details" on a specific invoice
  onSendInvoice, // callback if user wants to send invoice
  onUnpublish, // callback to unpublish an invoice
  getInvoiceStatusColor, // function that returns color classes based on invoice status
  onFilterChange, // callback for when the filter changes
  currentFilter = 'active', // default filter value

  // If you are also doing paging here:
  currentPage, // or pass in some pagination object
  onPageChange, // if you want the table to handle page changes
  ITEMS_PER_PAGE, // or you can keep this logic inside the parent, your choice
  onEditInvoice,

  // If you also want "onEditInvoice", you can add that here
  // ...
}) => {
  const [statusFilter, setStatusFilter] = useState(currentFilter);
  
  // Handle filter change
  const handleFilterChange = (newFilter) => {
    setStatusFilter(newFilter);
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };
  
  // Filter invoices based on status
  const filteredInvoices = invoices?.filter(invoice => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return invoice.status !== 'canceled';
    if (statusFilter === 'canceled') return invoice.status === 'canceled';
    return true;
  }) || [];
  
  if (!stats) return null; // or just fail gracefully if no stats

  return (
    <div className="space-y-6">
      {/* 1) Billing Overview Cards (Paid, Pending, Overdue) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* ----- Total Revenue ----- */}
        <div className="bg-white dark:bg-darkbg-800 rounded-lg p-6 shadow-md">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-grayteck-600 dark:text-grayteck-400">
                Total Revenue
              </p>
              <p className="mt-2 text-3xl font-bold text-grayteck-900 dark:text-grayteck-100">
                ${stats.totalPaid.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
              <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="mt-4 text-sm text-grayteck-600 dark:text-grayteck-400">
            Total paid invoices to date
          </div>
        </div>

        {/* ----- Pending Payments ----- */}
        <div className="bg-white dark:bg-darkbg-800 rounded-lg p-6 shadow-md">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-grayteck-600 dark:text-grayteck-400">
                Pending Payments
              </p>
              <p className="mt-2 text-3xl font-bold text-grayteck-900 dark:text-grayteck-100">
                ${stats.totalPending.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
              <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
          <div className="mt-4 text-sm text-grayteck-600 dark:text-grayteck-400">
            {stats.pendingCount ?? stats.upcomingPayments.length} pending invoices
          </div>
        </div>

        {/* ----- Overdue Payments ----- */}
        <div className="bg-white dark:bg-darkbg-800 rounded-lg p-6 shadow-md">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-grayteck-600 dark:text-grayteck-400">
                Overdue Payments
              </p>
              <p className="mt-2 text-3xl font-bold text-grayteck-900 dark:text-grayteck-100">
                ${stats.overdue.toFixed(2)}
              </p>
            </div>
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <div className="mt-4 text-sm text-grayteck-600 dark:text-grayteck-400">
            Requires immediate attention
          </div>
        </div>
      </div>

      {/* Saved Payment Methods (cards Stripe has on file for this client) */}
      <div className="bg-white dark:bg-darkbg-800 rounded-lg shadow-md">
        <div className="p-6 border-b border-grayteck-200 dark:border-grayteck-700">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-grayteck-600 dark:text-grayteck-400" />
            <h3 className="text-lg font-medium text-grayteck-900 dark:text-grayteck-100">
              Saved Payment Methods
            </h3>
          </div>
        </div>
        <div className="p-6">
          {(client?.payment_methods || []).length === 0 ? (
            <p className="text-sm text-grayteck-500 dark:text-grayteck-400">
              No cards on file for this client.
            </p>
          ) : (
            <div className="divide-y divide-grayteck-200 dark:divide-darkbg-600">
              {(client.payment_methods || []).map((pm) => {
                const isDefault = pm.pm_id === client.default_payment_method_id;
                return (
                  <div
                    key={pm.pm_id}
                    className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                  >
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-grayteck-100 dark:bg-darkbg-700">
                        <CreditCard className="w-4 h-4 text-grayteck-600 dark:text-grayteck-300" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-grayteck-900 dark:text-grayteck-100">
                          {pm.brand
                            ? pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)
                            : "Card"}{" "}
                          •••• {pm.last4}
                        </p>
                        <p className="text-xs text-grayteck-500 dark:text-grayteck-400">
                          Expires {String(pm.exp_month).padStart(2, "0")}/{pm.exp_year}
                        </p>
                      </div>
                    </div>
                    {isDefault && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                        <Star className="w-3 h-3" /> Default
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 2) Invoice List + "Create Invoice" Button */}
      <div className="bg-white dark:bg-darkbg-800 rounded-lg shadow-md">
        <div className="p-6 border-b border-grayteck-200 dark:border-grayteck-700">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-grayteck-900 dark:text-grayteck-100">
              Invoices
            </h3>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onCreateInvoice}
              className="px-4 py-2 bg-grayteck-600 text-white rounded-md hover:bg-grayteck-700 
                         flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Create Invoice</span>
            </motion.button>
          </div>
          
          {/* Invoice Status Filter */}
          <div className="mt-4">
            <InvoiceStatusFilter 
              activeFilter={statusFilter} 
              onFilterChange={handleFilterChange}
            />
          </div>
        </div>

        {/* Your Paginated Invoice Table */}
        <PaginatedInvoiceTable
          invoices={filteredInvoices}
          isLoading={isLoading}
          isPublishing={isPublishing}
          isUnpublishing={isUnpublishing}
          publishingInvoiceId={publishingInvoiceId}
          unpublishingInvoiceId={unpublishingInvoiceId}
          pagination={{
            // If you want to pass them:
            currentPage,
            totalPages: Math.ceil(
              (invoices?.length || 0) / (ITEMS_PER_PAGE || 20)
            ),
            totalItems: invoices?.length || 0,
            hasMore:
              ((currentPage || 0) + 1) * (ITEMS_PER_PAGE || 20) <
              (invoices?.length || 0),
          }}
          onPageChange={onPageChange}
          onViewDetails={onViewInvoice}
          onUnpublish={onUnpublish}
          onSendInvoice={onSendInvoice}
          getInvoiceStatusColor={getInvoiceStatusColor}
        />
      </div>
    </div>
  );
};

export default BillingSection;
